import { useQueries, type UseQueryOptions } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import type { components } from "@/api/schema";

type ReconGapsResponse = { gaps: components["schemas"]["ReconGap"][] };

/** shopee, lazada, tiktok — same order the Pipeline table's columns use. */
export const RECON_MARKETPLACES = ["shopee", "lazada", "tiktok"] as const;
export type ReconMarketplace = (typeof RECON_MARKETPLACES)[number];

export interface ReconGapsSelection {
  open: number;
}

function reconGapsQueryOptions(
  marketplace: ReconMarketplace,
): UseQueryOptions<ReconGapsResponse, unknown, ReconGapsSelection> {
  return {
    queryKey: ["dashboard", "recon-gaps", marketplace],
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/recon/gaps", {
        params: { query: { marketplace } },
      });
      if (error) throw error;
      return data;
    },
    select: (data) => ({ open: data.gaps.filter((g) => !g.resolved_at).length }),
    refetchInterval: 60_000,
  };
}

/**
 * GET /v1/recon/gaps?marketplace= for each of shopee/lazada/tiktok, each
 * reduced to its open-gap count. `useQueries` rather than one call per
 * marketplace via separate hooks, so a failure on one marketplace doesn't
 * blank the other two cells in the Pipeline table — matches the spec's
 * per-cell error-marker requirement.
 *
 * Returns the three query results in `RECON_MARKETPLACES` order; each
 * result's `.data` is `{ open }` once loaded.
 */
export function useReconGaps() {
  return useQueries({
    queries: RECON_MARKETPLACES.map(reconGapsQueryOptions),
  });
}
